import React, { FC, useCallback, useState, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Grid, makeStyles, MenuItem, Typography } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentAdapter from '@date-io/moment';
import {
  AdaptiveDatePicker,
  AvatarCard,
  NumberTextField,
  Select,
  TextField,
} from 'common/components';
import { CustomTheme } from 'common/ui/interfaces';
import { UploadImageModal } from 'common/modals';
import { IAttachmentImt } from 'common/interfaces/uploadFile';
import { genderTypeLabels, ActiveInactiveStatus, maritalStatusesLabels } from 'common/constants';
import inputLabels from 'common/messages/inputLabels';
import WebcamImageModal from 'common/modals/WebcamImageModal/WebcamImageModal';
import employeesMessages from 'common/messages/employeesMessages';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import DeleteImageModal from 'common/modals/DeleteImageModal/DeleteImageModal';
import { LazyQueryTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { BaseQueryFn, QueryDefinition } from '@reduxjs/toolkit/query';
import { IReqGetEmployeeId } from 'common/interfaces/employee.interfaces';
import { TagType } from 'common/enums/api.enums';

const useStyles = makeStyles((theme: CustomTheme) => ({
  avatar: {
    padding: theme.spacing(0),
    marginRight: theme.spacing(1.25),
  },
  infoWrapper: {
    [theme.breakpoints.up('md')]: {
      flexWrap: 'nowrap',
    },
  },
  preferredNameHint: {
    display: 'flex',
    alignItems: 'center',
  },
}));

interface IEmployeeInfoSection {
  generateEmployeeId?: boolean;
  getEmployeeIdentifier?: LazyQueryTrigger<
    QueryDefinition<IReqGetEmployeeId, BaseQueryFn, TagType, string, 'api'>
  >;
}

const EmployeeInfoSection: FC<IEmployeeInfoSection> = ({
  generateEmployeeId = false,
  getEmployeeIdentifier,
}) => {
  const timer = useRef<NodeJS.Timeout | null>(null);
  const renderIntlMessage = useRenderIntlMessage();
  const classes = useStyles();
  const { control, formState, setValue, watch } = useFormContext();
  const { errors } = formState;
  const firstName = watch('firstName');
  const lastName = watch('lastName');

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isOpenWebcamModal, setIsOpenWebcamModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const hasNameChanges = (prevName: string, newName: string): boolean => {
    if (!newName) {
      return false;
    }

    if (!prevName) {
      return true;
    }

    return !newName.startsWith(prevName.charAt(0));
  };

  const getEmployeeId = (fName: string, lName: string): void => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    if (getEmployeeIdentifier) {
      timer.current = setTimeout(() => {
        getEmployeeIdentifier({ firstName: fName, lastName: lName })
          .unwrap()
          .then(employeeId => {
            setValue('employeeIdentifier', employeeId, {
              shouldValidate: true,
              shouldDirty: true,
            });
          });
      }, 700);
    }
  };

  const onToggleUploadModal = useCallback(() => setIsUploadModalOpen(!isUploadModalOpen), [
    isUploadModalOpen,
    setIsUploadModalOpen,
  ]);

  const onToggleWebcamModal = useCallback(() => setIsOpenWebcamModal(!isOpenWebcamModal), [
    isOpenWebcamModal,
  ]);

  const onUploadSubmit = (uploadedImage: IAttachmentImt, isWebcam?: boolean) => {
    setValue('image', { id: uploadedImage.get('id'), url: uploadedImage.get('filePath') });
    (isWebcam ? onToggleWebcamModal : onToggleUploadModal)();
  };

  const onToggleDeleteModal = useCallback(() => setIsOpenDeleteModal(!isOpenDeleteModal), [
    isOpenDeleteModal,
  ]);

  const onDeleteSubmit = () => {
    setValue('image', null);
    onToggleDeleteModal();
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="button" color="textSecondary" gutterBottom>
          <FormattedMessage {...employeesMessages.personal} />
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          spacing={2}
          className={classes.infoWrapper}
        >
          <Grid item>
            <Controller
              name="image"
              render={({ field }) => (
                <AvatarCard
                  avatarUrl={field.value?.url}
                  height="162px"
                  onToggleUploadModal={onToggleUploadModal}
                  onToggleWebcamModal={onToggleWebcamModal}
                  onToggleDeleteModal={onToggleDeleteModal}
                />
              )}
            />
            {isUploadModalOpen && (
              <UploadImageModal
                id="upload-employee-photo-modal"
                isOpen={isUploadModalOpen}
                onSubmit={onUploadSubmit}
                onToggle={onToggleUploadModal}
                aspectRatio={1}
              />
            )}
            {isOpenWebcamModal && (
              <WebcamImageModal
                id="webcam-employee-photo-modal"
                isOpen={isOpenWebcamModal}
                onToggle={onToggleWebcamModal}
                onSubmit={uploadedImageUrl => onUploadSubmit(uploadedImageUrl, true)}
              />
            )}

            {isOpenDeleteModal && (
              <DeleteImageModal
                isOpen={isOpenDeleteModal}
                onClose={onToggleDeleteModal}
                onSubmit={onDeleteSubmit}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <Controller
                  control={control}
                  name="firstName"
                  render={({ field }) => (
                    <TextField
                      variant="outlined"
                      label={<FormattedMessage {...inputLabels.firstName} />}
                      onBlur={field.onBlur}
                      onChange={event => {
                        const newFirstName = event.target.value;

                        if (
                          generateEmployeeId &&
                          lastName &&
                          hasNameChanges(firstName, newFirstName)
                        ) {
                          getEmployeeId(newFirstName, lastName);
                        }

                        field.onChange(event);
                      }}
                      autoComplete="off"
                      fullWidth
                      error={!!errors.firstName}
                      helperText={renderIntlMessage(errors.firstName?.message)}
                      value={field.value}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <Controller
                  control={control}
                  name="lastName"
                  render={({ field }) => (
                    <TextField
                      variant="outlined"
                      label={<FormattedMessage {...inputLabels.lastName} />}
                      onBlur={field.onBlur}
                      onChange={event => {
                        const newLastName = event.target.value;

                        if (
                          generateEmployeeId &&
                          firstName &&
                          hasNameChanges(lastName, newLastName)
                        ) {
                          getEmployeeId(firstName, newLastName);
                        }

                        field.onChange(event);
                      }}
                      autoComplete="off"
                      fullWidth
                      error={!!errors.lastName}
                      helperText={renderIntlMessage(errors.lastName?.message)}
                      value={field.value}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6} sm={3}>
                <Controller
                  control={control}
                  name="active"
                  render={({ field }) => (
                    <Select
                      label={<FormattedMessage {...inputLabels.status} />}
                      value={field.value ? 1 : 0}
                      name={field.name}
                      fullWidth
                      defaultValue=""
                      onBlur={field.onBlur}
                      onChange={e => {
                        field.onChange(!!e);
                      }}
                      labelId="status"
                    >
                      {ActiveInactiveStatus.map(status => (
                        <MenuItem value={Number(status.value)} key={status.key}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  control={control}
                  name="preferredName"
                  render={({ field }) => (
                    <TextField
                      variant="outlined"
                      label={<FormattedMessage {...inputLabels.preferredName} />}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      autoComplete="off"
                      fullWidth
                      error={!!errors.preferredName}
                      helperText={renderIntlMessage(errors.preferredName?.message)}
                      value={field.value}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} className={classes.preferredNameHint}>
                <Typography component="p" color="textSecondary">
                  <FormattedMessage
                    values={{
                      replacedField: (
                        <Typography variant="h6" component="span" color="inherit">
                          <FormattedMessage {...inputLabels.preferredName} />
                        </Typography>
                      ),
                      newField: (
                        <Typography component="span" color="inherit">
                          <FormattedMessage {...inputLabels.firstName} />
                        </Typography>
                      ),
                    }}
                    {...employeesMessages.willBeDisplayedInsteadOfText}
                  />
                </Typography>
              </Grid>

              <Grid item xs={6} sm={4}>
                <Controller
                  control={control}
                  name="gender"
                  render={({ field }) => (
                    <Select
                      label={<FormattedMessage {...inputLabels.gender} />}
                      value={field.value}
                      name={field.name}
                      variant="outlined"
                      fullWidth
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      error={!!errors.gender}
                      helperText={renderIntlMessage(errors.gender?.message)}
                    >
                      {genderTypeLabels.getSelectOptions()}
                    </Select>
                  )}
                />
              </Grid>

              <Grid item xs={6} sm={4}>
                <MuiPickersUtilsProvider utils={MomentAdapter}>
                  <Controller
                    name="birthday"
                    control={control}
                    defaultValue={null}
                    render={({ field }) => (
                      <AdaptiveDatePicker
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        disableFuture
                        label={<FormattedMessage {...inputLabels.dateOfBirth} />}
                        error={!!errors.birthday}
                        helperText={renderIntlMessage(errors.birthday?.message)}
                        openTo="year"
                      />
                    )}
                  />
                </MuiPickersUtilsProvider>
              </Grid>

              <Grid item xs={6} sm={4}>
                <Controller
                  control={control}
                  name="maritalStatus"
                  render={({ field }) => (
                    <Select
                      label={<FormattedMessage {...inputLabels.maritalStatus} />}
                      value={field.value}
                      name={field.name}
                      variant="outlined"
                      fullWidth
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      error={!!errors.maritalStatus}
                      helperText={renderIntlMessage(errors.maritalStatus?.message)}
                    >
                      {maritalStatusesLabels.values.map(maritalStatus => (
                        <MenuItem key={maritalStatus.key} value={maritalStatus.value}>
                          {maritalStatusesLabels.translate(maritalStatus.value)}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <Controller
                  control={control}
                  name="exemptionsNumber"
                  render={({ field }) => (
                    <NumberTextField
                      numberFormatProps={{
                        decimalScale: 0,
                        fixedDecimalScale: true,
                        allowLeadingZeros: false,
                        allowNegative: false,
                        isNumericString: true,
                        maxLength: 11,
                        type: 'text',
                        format: (inputValue: string) => inputValue,
                      }}
                      variant="outlined"
                      label={<FormattedMessage {...employeesMessages.exemptionsNumber} />}
                      value={Number(field.value)}
                      onChange={field.onChange}
                      fullWidth
                      error={!!errors.exemptionsNumber}
                      helperText={renderIntlMessage(errors.exemptionsNumber?.message)}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  control={control}
                  name="federalWithholding"
                  render={({ field }) => (
                    <NumberTextField
                      numberFormatProps={{
                        decimalScale: 0,
                        fixedDecimalScale: true,
                        allowLeadingZeros: false,
                        allowNegative: false,
                        isNumericString: true,
                        maxLength: 11,
                        type: 'text',
                        format: (inputValue: string) => inputValue,
                      }}
                      variant="outlined"
                      label={<FormattedMessage {...employeesMessages.federalWithholding} />}
                      value={Number(field.value)}
                      onChange={field.onChange}
                      fullWidth
                      error={!!errors.federalWithholding}
                      helperText={renderIntlMessage(errors.federalWithholding?.message)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EmployeeInfoSection;
